const createCategoryReportThermal = (data, language, settings) => {
  // Ensure returns data exists
  const { categories = [], summary = {} } = data || {};
  const returns = data?.returns || { total_amount: 0, count: 0, percentage: 0 };
  
  // Check if any items are marked as returns
  const hasReturnItems = categories.some(cat => 
    (cat.items || []).some(item => item.is_return)
  );
  
  // Ensure summary is properly structured with defaults
  const safetyCheckedSummary = {
    total_revenue: summary.total_revenue || 0,
    total_orders: summary.total_orders || 0,
    total_items_sold: summary.total_items_sold || 0
  };
  
  // Include all categories (including those with no revenue or returned items)
  const allCategories = categories
    .map(cat => ({
      ...cat,
      // Ensure top_items always exists and has the expected shape
      items: (cat.items || []).map(item => ({
        id: item.id || 0,
        name: item.name || '',
        name_ar: item.name_ar || '',
        quantity: item.quantity || 0,
        revenue: item.revenue || 0, 
        order_count: item.order_count || 0,
        revenue_share: item.revenue_share || 0,
        is_return: item.is_return || false
      }))
    }))
    .sort((a, b) => b.total_revenue - a.total_revenue);
  
  // Check if returns data exists and has count property
  const hasReturns = returns && (returns.count > 0 || returns.total_amount > 0);
  
  // Calculate totals with and without returns
  const totalGrossSales = safetyCheckedSummary.total_revenue;
  const totalReturns = returns.total_amount || 0;
  const totalNetSales = totalGrossSales - totalReturns;
  
  // Format date in Gregorian format (DD/MM/YYYY)
  const currentDate = new Date();
  const day = String(currentDate.getDate()).padStart(2, '0');
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const year = currentDate.getFullYear();
  const hours = String(currentDate.getHours()).padStart(2, '0');
  const minutes = String(currentDate.getMinutes()).padStart(2, '0');
  
  const date = `${day}/${month}/${year}`;
  const time = `${hours}:${minutes}`;

  // Get paper dimensions from settings
  const { printing = {}, restaurant = {} } = settings || {};
  const paperWidth = printing?.paper_size || '80mm';
  // Set font size - higher values appear larger on thermal prints
  const fontSize = '8';

  // Use exact paper width from settings
  const pageWidth = parseInt(paperWidth);
  
  // Format currency function
  const formatCurrency = (value) => {
    return parseFloat(value).toFixed(2);
  };

  // Get receipt number (use timestamp if not available)
  const receiptNumber = data.receipt_number || Date.now().toString().slice(-6);

  return `
    <!DOCTYPE html>
    <html style="margin:0;padding:0;width:${pageWidth}mm;">
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
      <style>
        @page {
          margin: 0;
          padding: 0;
          size: ${pageWidth}mm auto;
        }
        @media print {
          @page {
            margin: 0 5mm; /* Explicitly set page margins for printing */
          }
        }
        html, body {
          font-family: 'Courier New', monospace;
          font-size: ${fontSize}pt;
          font-weight: bold;
          width: 100%;
          margin: 0 5mm; /* Add 5mm margin on both left and right */
          padding: 0;
          direction: ${language === 'ar' ? 'rtl' : 'ltr'};
          overflow-x: hidden; /* Prevent horizontal overflow */
        }

        table {
          width: 100%;
          border-collapse: collapse;
          border-spacing: 0;
          table-layout: fixed;
          margin: 0;
          padding: 0;
        }
        td, th {
          padding: 1px;
          margin: 0;
          text-align: ${language === 'ar' ? 'right' : 'left'};
          font-size: ${fontSize}pt;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .center {
          text-align: center;
          width: 100%;
        }
        .right {
          text-align: right;
        }
        .bold {
          font-weight: bold;
        }
        .divider {
          border-top: 1px dashed #000;
          height: 1px;
          margin-top: 1px;
          width: 100%;
        }
        .solid-divider {
          border-top: 1px solid #000;
          height: 1px;
          margin-top: 1px;
          width: 100%;
        }
        .small {
          font-size: ${parseInt(fontSize) - 3}pt;
        }
        /* Utility classes */
        * {
          box-sizing: border-box;
        }
      </style>
    </head>
    <body>
      <table>
        <tr style="margin-top:0;padding-top:0;">
          <td class="center bold">${language === 'ar' ? restaurant?.name_ar || 'المطعم' : restaurant?.name || 'Restaurant'}</td>
        </tr>
        <tr>
          <td class="center bold">${language === 'ar' ? 'تقرير المبيعات حسب الفئة' : 'Category Sales Report'}</td>
        </tr>
        <tr>
          <td class="center small">${language === 'ar' ? 'رقم التقرير: ' : 'Report #: '}${receiptNumber}</td>
        </tr>
        <tr>
          <td class="center small">${date} ${time}</td>
        </tr>
        <tr>
          <td class="center small">${language === 'ar' ? 'الفترة: ' : 'Period: '}${data.date_range && data.date_range.start ? data.date_range.start : ''} ${(data.date_range && (data.date_range.start || data.date_range.end)) ? (language === 'ar' ? 'إلى' : 'to') : ''} ${data.date_range && data.date_range.end ? data.date_range.end : ''}</td>
        </tr>
      </table>
      
      <div class="divider"></div>
      
      <!-- Summary -->
      <table>
        <tr>
          <td>${language === 'ar' ? 'إجمالي المبيعات' : 'Total Revenue'}</td>
          <td class="right">${formatCurrency(safetyCheckedSummary.total_revenue)}</td>
        </tr>
        <tr>
          <td>${language === 'ar' ? 'عدد الطلبات' : 'Total Orders'}</td>
          <td class="right">${safetyCheckedSummary.total_orders}</td>
        </tr>
        <tr>
          <td>${language === 'ar' ? 'المنتجات المباعة' : 'Items Sold'}</td>
          <td class="right">${safetyCheckedSummary.total_items_sold}</td>
        </tr>
      </table>
      
      ${hasReturns ? `
      <div class="divider"></div>
      
      <!-- Returns Summary -->
      <table>
        <tr>
          <td colspan="2" class="center">${language === 'ar' ? 'ملخص المرتجعات' : 'Returns Summary'}</td>
        </tr>
        <tr>
          <td>${language === 'ar' ? 'إجمالي المرتجعات' : 'Total Returns'}</td>
          <td class="right">${formatCurrency(returns.total_amount)}</td>
        </tr>
        <tr>
          <td>${language === 'ar' ? 'عدد الطلبات المرتجعة' : 'Number of Returns'}</td>
          <td class="right">${returns.count}</td>
        </tr>
        <tr>
          <td>${language === 'ar' ? 'نسبة المرتجعات' : 'Returns Percentage'}</td>
          <td class="right">${(returns.percentage || 0).toFixed(2)}%</td>
        </tr>
      </table>
      ` : ''}
      
      <div class="divider"></div>
      
      <!-- Column Headers -->
      <table class="small full-width">
        <tr class="bold small">
          <td width="50%">${language === 'ar' ? 'الصنف' : 'Item'}</td>
          <td width="15%" class="right">${language === 'ar' ? 'الكمية' : 'Qty'}</td>
          <td width="35%" class="right">${language === 'ar' ? 'القيمة' : 'Value'}</td>
        </tr>
      </table>
      
      <div class="solid-divider"></div>
      
      <!-- Categories and Items (including returns) -->
      ${allCategories.map((category, index) => {
        // Check if this category has any return items
        const categoryReturnItems = (category.items || []).filter(item => item.is_return);
        const hasCategoryReturns = categoryReturnItems.length > 0;
        
        return `
      <table>
        <tr>
          <td colspan="3" class="center bold">${language === 'ar' ? category.name_ar : category.name}</td>
        </tr>
      </table>
      
      <table class="small full-width">
        ${(category.items || []).map(item => `
        <tr>
          <td width="50%">${language === 'ar' ? item.name_ar : item.name}${item.is_return ? ` <strong>(${language === 'ar' ? 'مرتجع' : 'Return'})</strong>` : ''}</td>
          <td width="15%" class="right">${item.quantity || 0}</td>
          <td width="35%" class="right">${item.is_return ? '-' : ''}${formatCurrency(item.revenue || 0)}</td>
        </tr>
        `).join('')}
        
        <tr class="bold">
          <td>${language === 'ar' ? 'مجموع' : 'Subtotal'}</td>
          <td></td>
          <td class="right">${formatCurrency(category.total_revenue)}</td>
        </tr>
        ${hasCategoryReturns ? `
        <tr>
          <td colspan="3" class="small">${language === 'ar' ? 'ملاحظة: المجموع يشمل المرتجعات' : 'Note: Total includes returns'}</td>
        </tr>
        ` : ''}
      </table>
      
      <div class="solid-divider"></div>
      `}).join('')}
      
      <!-- Totals Section -->
      <table class="bold full-width">
        <tr>
          <td>${language === 'ar' ? 'إجمالي المبيعات' : 'Gross Total'}</td>
          <td class="right">${formatCurrency(totalGrossSales)}</td>
        </tr>
        ${hasReturns ? `
        <tr>
          <td>${language === 'ar' ? 'إجمالي المرتجعات' : 'Total Returns'}</td>
          <td class="right">- ${formatCurrency(totalReturns)}</td>
        </tr>
        <tr class="bold">
          <td>${language === 'ar' ? 'الصافي بعد المرتجعات' : 'Net Total'}</td>
          <td class="right">${formatCurrency(totalNetSales)}</td>
        </tr>
        ` : ''}
      </table>
      
      <div class="divider"></div>
      
      <!-- Verification Section -->
      <div class="small">
        <p>${language === 'ar' ? 'تحقق من المجاميع:' : 'Verification Totals:'}</p>
        <p>${language === 'ar' ? 'مجموع الفئات المعروضة:' : 'Shown Categories Sum:'} ${formatCurrency(categories.reduce((sum, cat) => sum + (cat.total_revenue || 0), 0))}</p>
        <p>${language === 'ar' ? 'المجموع الكلي المعروض:' : 'Shown Grand Total:'} ${formatCurrency(totalGrossSales)}</p>
        <p>${language === 'ar' ? 'عدد الفئات المعروضة:' : 'Categories Shown:'} ${categories.length}</p>
        <p>${language === 'ar' ? 'عدد الفئات في النظام:' : 'Total Categories:'} ${(data.all_categories?.length || categories.length)}</p>
        <p>${language === 'ar' ? 'عدد الطلبات:' : 'Order Count:'} ${summary.total_orders}</p>
        ${hasReturns ? `<p><strong>${language === 'ar' ? 'إجمالي المرتجعات:' : 'Total Returns:'} ${formatCurrency(returns.total_amount)}</strong></p>` : ''}
        ${hasReturnItems ? `<p><i>${language === 'ar' ? 'ملاحظة: بعض المرتجعات قد تكون مدرجة في مجاميع الفئات' : 'Note: Some return items may be included in category totals'}</i></p>` : ''}
      </div>
      
      <div class="divider"></div>
      
      <!-- Footer -->
      <table class="bold full-width">
        <tr>
          <td class="center">${language === 'ar' ? 'شكراً لكم' : 'Thank You'}</td>
        </tr>
      </table>
    </body>
    </html>
  `;
};

export default createCategoryReportThermal;